import React from "react";
import { getAllBuildingPresets } from "@/three/config/buildingPresets";
import { usePresetStore } from "@/stores/presetStore";
import type { BuildingStructureBox } from "@/utils/supabase";

// TopRightOverlay와 동일한 빈티지 문서 스타일
const VINTAGE_STYLES = {
  label: {
    color: "#666655",
    fontSize: "10px",
    fontWeight: 600 as const,
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
    marginBottom: "4px",
  },
  select: {
    width: "100%",
    padding: "6px 8px",
    fontSize: "12px",
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    background: "#faf8f0",
    border: "1px solid #d4c9b0",
    borderRadius: "3px",
    color: "#2a2a2a",
    cursor: "pointer",
  },
  input: {
    flex: 1,
    padding: "6px 8px",
    fontSize: "12px",
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    background: "#faf8f0",
    border: "1px solid #d4c9b0",
    borderRadius: "3px",
    color: "#2a2a2a",
  },
  button: {
    padding: "6px 12px",
    fontSize: "11px",
    fontWeight: 600,
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    background: "#8b7355",
    border: "none",
    borderRadius: "3px",
    color: "#fff",
    cursor: "pointer",
  },
  listItem: {
    padding: "6px 8px",
    borderBottom: "1px dashed #c4b9a0",
    cursor: "pointer",
  },
};

interface EditorBox {
  id: string;
  position: [number, number, number];
  scale: [number, number, number];
}

interface EditorPresetSelectorProps {
  boxes: EditorBox[];
  setBoxes: React.Dispatch<React.SetStateAction<EditorBox[]>>;
  setSelectedBoxId: React.Dispatch<React.SetStateAction<string | null>>;
}

/**
 * 빈티지 문서 스타일 프리셋 선택기
 * TopRightOverlay와 동일한 디자인 시스템 사용
 */
export function EditorPresetSelector({
  boxes,
  setBoxes,
  setSelectedBoxId,
}: EditorPresetSelectorProps) {
  const presets = getAllBuildingPresets();
  const { customPresets, addPreset, removePreset } = usePresetStore();

  const [selectedPresetIndex, setSelectedPresetIndex] =
    React.useState<number>(-1);
  const [selectedCustomPresetId, setSelectedCustomPresetId] = React.useState<
    string | null
  >(null);
  const [presetName, setPresetName] = React.useState("");

  const handlePresetSelect = React.useCallback(
    (index: number) => {
      if (index < 0) {
        setBoxes([]);
        setSelectedPresetIndex(-1);
        return;
      }
      const preset = presets[index];
      if (!preset) return;

      const newBoxes: EditorBox[] = preset.structure.map((box, i) => ({
        id: `box-${i}-${Date.now()}`,
        position: [
          box.position[0] - box.scale[0] / 2,
          box.position[1] - box.scale[1] / 2,
          box.position[2] - box.scale[2] / 2,
        ] as [number, number, number],
        scale: [...box.scale] as [number, number, number],
      }));

      setBoxes(newBoxes);
      setSelectedPresetIndex(index);
      setSelectedCustomPresetId(null);
      setSelectedBoxId(newBoxes.length > 0 ? newBoxes[0].id : null);
    },
    [presets, setBoxes, setSelectedBoxId]
  );

  const handleCustomPresetSelect = React.useCallback(
    (presetId: string | null) => {
      if (!presetId) return;
      const preset = customPresets.find((p) => p.id === presetId);
      if (!preset) return;

      const newBoxes: EditorBox[] = preset.structure.map((box, i) => ({
        id: `box-${i}-${Date.now()}`,
        position: [
          box.position[0] - box.scale[0] / 2,
          box.position[1] - box.scale[1] / 2,
          box.position[2] - box.scale[2] / 2,
        ] as [number, number, number],
        scale: [...box.scale] as [number, number, number],
      }));

      setBoxes(newBoxes);
      setSelectedCustomPresetId(presetId);
      setSelectedPresetIndex(-1);
      setSelectedBoxId(newBoxes.length > 0 ? newBoxes[0].id : null);
    },
    [customPresets, setBoxes, setSelectedBoxId]
  );

  const handleSaveAsPreset = React.useCallback(() => {
    if (boxes.length === 0) return;
    const name = presetName.trim() || `도면 ${customPresets.length + 1}호`;
    const structure: BuildingStructureBox[] = boxes.map((box) => ({
      position: [
        box.position[0] + box.scale[0] / 2,
        box.position[1] + box.scale[1] / 2,
        box.position[2] + box.scale[2] / 2,
      ] as [number, number, number],
      scale: [...box.scale] as [number, number, number],
    }));
    addPreset({ name, description: `${boxes.length}개`, structure });
    setPresetName("");
  }, [boxes, presetName, customPresets.length, addPreset]);

  return (
    <div style={{ fontFamily: "'Noto Serif KR', 'Georgia', serif" }}>
      {/* 기본형 선택 */}
      <div style={{ marginBottom: "12px" }}>
        <div style={VINTAGE_STYLES.label}>기본형</div>
        <select
          value={selectedPresetIndex}
          onChange={(e) => handlePresetSelect(parseInt(e.target.value, 10))}
          style={VINTAGE_STYLES.select}
        >
          <option value={-1}>— 신규 설계 —</option>
          {presets.map((preset, index) => (
            <option key={preset.meshIndex} value={index}>
              {preset.name}
            </option>
          ))}
        </select>
      </div>

      {/* 저장된 도면 */}
      {customPresets.length > 0 && (
        <div style={{ marginBottom: "12px" }}>
          <div style={VINTAGE_STYLES.label}>
            저장된 도면 ({customPresets.length})
          </div>
          <div
            style={{
              maxHeight: "60px",
              overflowY: "auto",
              background: "#faf8f0",
              border: "1px solid #d4c9b0",
              borderRadius: "3px",
            }}
          >
            {customPresets.map((preset, idx) => (
              <div
                key={preset.id}
                onClick={() => handleCustomPresetSelect(preset.id)}
                style={{
                  ...VINTAGE_STYLES.listItem,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background:
                    selectedCustomPresetId === preset.id
                      ? "#e8e4d8"
                      : "transparent",
                  borderBottom:
                    idx < customPresets.length - 1
                      ? "1px dashed #c4b9a0"
                      : "none",
                }}
              >
                <span
                  style={{
                    color: "#8b7355",
                    fontWeight: 600,
                    marginRight: "8px",
                  }}
                >
                  {idx + 1}.
                </span>
                <span style={{ flex: 1, color: "#2a2a2a", fontSize: "12px" }}>
                  {preset.name}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removePreset(preset.id);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#c44",
                    cursor: "pointer",
                    fontSize: "14px",
                    padding: "0 4px",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 도면 저장 */}
      <div>
        <div style={VINTAGE_STYLES.label}>도면 저장</div>
        <div style={{ display: "flex", gap: "6px" }}>
          <input
            type="text"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder="도면명 입력..."
            style={VINTAGE_STYLES.input}
          />
          <button
            onClick={handleSaveAsPreset}
            disabled={boxes.length === 0}
            style={{
              ...VINTAGE_STYLES.button,
              background: boxes.length === 0 ? "#ccc" : "#8b7355",
              cursor: boxes.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
